import { useCallback, useEffect, useRef, useState } from 'react';

export interface UsePermissionStatusResult {
  location: PermissionState | 'prompt' | 'unsupported';
  notifications: NotificationPermission | 'unsupported';
  refresh: () => void;
}

function getInitialNotifications(): NotificationPermission | 'unsupported' {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

export function usePermissionStatus(): UsePermissionStatusResult {
  const [location, setLocation] = useState<PermissionState | 'prompt' | 'unsupported'>('prompt');
  const [notifications, setNotifications] = useState<NotificationPermission | 'unsupported'>(
    getInitialNotifications
  );
  const locationStatusRef = useRef<PermissionStatus | null>(null);

  const refresh = useCallback(async () => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setNotifications(Notification.permission);
    } else {
      setNotifications('unsupported');
    }

    if (typeof navigator !== 'undefined' && 'permissions' in navigator) {
      try {
        if (locationStatusRef.current) {
          locationStatusRef.current.onchange = null;
        }
        const result = await navigator.permissions.query({ name: 'geolocation' });
        locationStatusRef.current = result;
        setLocation(result.state);
        result.onchange = () => {
          setLocation(result.state);
        };
      } catch {
        setLocation('prompt');
      }
    } else {
      setLocation('prompt');
    }
  }, []);

  useEffect(() => {
    let active = true;

    if (typeof navigator !== 'undefined' && 'permissions' in navigator) {
      void (async () => {
        try {
          if (locationStatusRef.current) {
            locationStatusRef.current.onchange = null;
          }
          const result = await navigator.permissions.query({ name: 'geolocation' });
          if (!active) return;
          locationStatusRef.current = result;
          setLocation(result.state);
          result.onchange = () => {
            setLocation(result.state);
          };
        } catch {
          if (active) {
            setLocation('prompt');
          }
        }
      })();
    }

    return () => {
      active = false;
      if (locationStatusRef.current) {
        locationStatusRef.current.onchange = null;
      }
    };
  }, []);

  return {
    location,
    notifications,
    refresh,
  };
}
